/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import './chat-message-form.scss';

import { useMutation } from '@apollo/client';
import { usePrevious } from '@uidotdev/usehooks';
import { kebabCase } from 'lodash';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import { useStoredConversationMessageItem } from '../../../data/data/conversations/use-stored-conversation-message-item';
import {
  AgendaAppointmentModalAction,
  ChatMessageActionType,
} from '../../../data/data/enums';
import {
  AgendaAppointmentInput,
  AppointmentModalData,
  ChatMessageCreateFileModalData,
  CreateChatDefaultModalData,
} from '../../../data/data/types';
import {
  ConsultType,
  ConversationChatMessageItemDataFragment,
  ConversationItemDataFragment,
  CreateConversationChatMessageItemDocument,
  SlotType,
} from '../../../data/types/api/graphql';
import { AlertMessageType } from '../../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../../button/button';
import { InputTextAreaController } from '../../form/controllers/input-textarea';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';
import { useChatMessageForm } from './form/use-chat-message-form';

export const getAppointmentInput = (
  value: ConversationItemDataFragment | null | undefined
): AgendaAppointmentInput => ({
  title: value?.title,
  patient: value?.patient
    ? {
        id: value.patient.id,
      }
    : null,
  conversation: value?.id
    ? {
        id: value.id,
      }
    : null,
  consultType: ConsultType.Physical,
  disableHonorific: false,
});

type ChatMessageFormProps = {
  healthProviderId: string | null | undefined;
  conversationItem: ConversationItemDataFragment | null | undefined;
  onSaved?: (message: ConversationChatMessageItemDataFragment) => void;
  showAppointmentModal?: (
    slotType: SlotType,
    value: AppointmentModalData | null
  ) => void;
  showCreateChatDefaultModal?: (
    value: CreateChatDefaultModalData | null
  ) => void;
  showCreateChatFileModal?: (
    value: ChatMessageCreateFileModalData | null
  ) => void;
};

export function ChatMessageForm({
  healthProviderId,
  conversationItem,
  showAppointmentModal,
  showCreateChatDefaultModal,
  showCreateChatFileModal,
  onSaved,
}: ChatMessageFormProps) {
  const intl = useIntl();
  const shiftEnter = false;

  const { showAlertMessage } = useAppContext();

  const [actionsOpen, setActionsOpen] = useState<boolean>(false);

  const onToggleActions = useCallback(() => {
    setActionsOpen(prevState => !prevState);
  }, []);

  const onHandleCreateChatDefault = useCallback(
    (option: ChatMessageActionType) => {
      showCreateChatDefaultModal?.({
        type: option,
        healthProviderId,
        conversationItem,
      });
    },
    [conversationItem, healthProviderId, showCreateChatDefaultModal]
  );

  const onHandleAppointment = useCallback(() => {
    showAppointmentModal?.(SlotType.Common, {
      action: AgendaAppointmentModalAction.CREATE,
      healthProviderId,
      agendaAppointmentItem: getAppointmentInput(conversationItem),
    });
  }, [conversationItem, healthProviderId, showAppointmentModal]);

  const onHandleCreateChatFile = useCallback(() => {
    showCreateChatFileModal?.({ conversationItem });
  }, [conversationItem, showCreateChatFileModal]);

  const { form, formIsValid } = useChatMessageForm();

  const [
    createConversationChatMessageItem,
    { loading: createConversationChatMessageItemLoading },
  ] = useMutation(CreateConversationChatMessageItemDocument);

  const { save, load, remove } = useStoredConversationMessageItem();

  const onHandleSubmit = useCallback(() => {
    // TODO RESET LOCAL STORAGE

    form.handleSubmit(async () => {
      const values = form.getValues();
      remove(conversationItem?.id ?? '');
      try {
        const result = await createConversationChatMessageItem({
          variables: {
            conversationId: conversationItem?.id,
            text: values.text,
          },
        });

        if (result.data?.messageTextCreate?.message) {
          onSaved?.(result.data?.messageTextCreate?.message);

          form.reset();
        } else {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: intl.formatMessage({
              id: 'common.error',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    remove,
    conversationItem?.id,
    createConversationChatMessageItem,
    onSaved,
    showAlertMessage,
    intl,
  ]);

  const onHandleFormKeyUp = useCallback(
    (e: KeyboardEvent<HTMLFormElement>) => {
      if (shiftEnter && e.code === 'Enter' && !e.shiftKey) {
        onHandleSubmit();
      }
    },
    [onHandleSubmit, shiftEnter]
  );

  // reset form on conversation change
  const prevConversationItem = usePrevious(conversationItem);
  const text = form.watch('text');

  useEffect(() => {
    if (
      !prevConversationItem ||
      (prevConversationItem &&
        conversationItem &&
        prevConversationItem.id !== conversationItem.id)
    ) {
      const loadedText = load(conversationItem?.id);
      form.reset();
      form.setValue('text', loadedText ?? '', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [conversationItem, form, load, prevConversationItem]);

  return (
    <FormProvider {...form}>
      <form className="chat-message-form" onKeyUp={onHandleFormKeyUp}>
        <div className="chat-message-form__menu">
          <Button
            type={ButtonType.TRANSPARENT}
            icon={actionsOpen ? ButtonIcon.CLOSE : ButtonIcon.ELLIPSIS_VERTICAL}
            onClick={onToggleActions}
          />
        </div>
        <div className="chat-message-form__input">
          <InputTextAreaController
            onBlur={() => save(conversationItem?.id ?? '', text)}
            shiftEnter={shiftEnter}
            type="textarea"
            name="text"
            placeholder={intl.formatMessage({ id: 'chat.type-message' })}
          />
        </div>
        <div className="chat-message-form__submit">
          <Button
            useAsSubmit
            icon={
              createConversationChatMessageItemLoading
                ? ButtonIcon.LOADING
                : ButtonIcon.PAPERPLANE
            }
            disabled={
              !conversationItem ||
              !formIsValid ||
              createConversationChatMessageItemLoading
            }
            onClick={onHandleSubmit}
          />
        </div>
        {actionsOpen && (
          <div className="chat-message-form__actions">
            {Object.values(ChatMessageActionType)
              .filter(option => option !== ChatMessageActionType.DEFAULT)
              .map(option => {
                let icon = ButtonIcon.NONE;
                switch (option) {
                  case ChatMessageActionType.APPOINTMENT:
                    icon = ButtonIcon.CALENDAR;
                    break;
                  case ChatMessageActionType.APPOINTMENT_MISSED:
                    icon = ButtonIcon.CALENDAR_CANCEL;
                    break;
                  case ChatMessageActionType.APPOINTMENT_NO_SHOW:
                    icon = ButtonIcon.CALENDAR_EXCLAMATION;
                    break;
                  case ChatMessageActionType.URINE_QUESTIONNAIRE:
                    icon = ButtonIcon.DROPLET;
                    break;
                  case ChatMessageActionType.EXPLAIN_COMPLAINT:
                    icon = ButtonIcon.COMMENT;
                    break;
                  default:
                    icon = ButtonIcon.NONE;
                    break;
                }

                return (
                  <Button
                    key={option}
                    icon={icon}
                    inverted
                    onClick={() => onHandleCreateChatDefault(option)}
                  >
                    <CustomFormattedMessage
                      id={`conversation.schedule-appointment.${kebabCase(
                        option
                      )}.title`}
                    />
                  </Button>
                );
              })}

            <Button
              icon={ButtonIcon.CALENDAR_ADD}
              inverted
              onClick={() => onHandleAppointment()}
            >
              <CustomFormattedMessage id="common.chat-message.create-appointment" />
            </Button>

            <Button
              icon={ButtonIcon.UPLOAD}
              inverted
              onClick={() => onHandleCreateChatFile()}
            >
              <CustomFormattedMessage id="conversation.chat-message-create.file-upload.title" />
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
}
